// Ported from obsidian-plugin/src/components/RetrievalCard.tsx.
// Obsidian adaptations:
//   - new Notice(...) → toast from sonner
//   - Vault "Save to MalpediaRAG folder" export → download a .md blob via a synthetic <a download>
//
// The card is two layers:
//   - the step timeline (StepTimeline) — what is happening, live, one row per
//     stage; collapses into a one-line summary once the answer streams;
//   - per-stage deep-dive, shown when a timeline row is expanded: the plan
//     (entities, strategy contribution, reasoning) on "Planning", the read
//     summary on an agent's "Researching", score thresholds on "Reranking".
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import type {
  PipelineStep,
  PlanningEvent,
  RoutingEvent,
  StepDetail,
  GraphTraversalEvent,
  ProfileEvent,
  ScoreThreshold,
  SourceDoc,
  DetectedIOC,
} from '../../types';
import { countSourceDocs, isSummarySource } from '../../lib/sourceDocs';
import {
  MODE_LABELS,
  STRATEGY_TOOLTIPS,
  displayName,
  familyOf,
  toolArgSummary,
} from './strategies';
import type { StageKey, Timeline, TimelineMarks } from './timelineModel';
import { deriveTimeline, formatSeconds, timelineSummary } from './timelineModel';
import { StepIcon, StepTimeline, useNowSeconds } from './StepTimeline';

const QUERY_TYPE_TOOLTIPS: Record<string, string> = {
  relationship:           'Connections or links between two named entities',
  activity_timeline:      'Recent activity, campaigns, or timeline of an entity',
  technique_context:      'Attack techniques, TTPs, or methods',
  entity_profile:         'Profile of a single malware family or threat actor',
  comparison:             'Side-by-side comparison of two or more entities',
  ioc_lookup:             'Lookup of a specific indicator (IP, hash, domain)',
  attribution:            'Who operates or deploys a malware family',
  ioc_pivot:              'Pivot from an indicator to connected threats',
  ioc_correlation:        'Entities sharing infrastructure or indicators',
  detection:              'Identify threats from a set of indicators',
  campaign_analysis:      'Reports mentioning multiple entities together',
  evolution:              'How an entity changed or developed over time',
  comprehensive_profile:  'Everything about a single entity — full deep-dive',
  trend_summary:          'Activity round-up over a date range, no single entity',
  general:                'Broad or conceptual query',
  agent:                  'No fixed plan — the model researched with retrieval tools',
};

const SOURCE_TOOLTIPS: Record<string, string> = {
  heuristic: 'Matched a common pattern (entity lookup, IOC, temporal) — no LLM needed',
  llm:       'An LLM analyzed your query and designed a custom retrieval plan',
  manual:    'You picked the strategies yourself',
  agent:     'The model chose its retrieval tools itself',
};

const DEPTH_TOOLTIPS: Record<string, string> = {
  narrow:     'Minimal retrieval — single item lookup (5 candidates)',
  normal:     'Focused analysis of 1-2 entities (10 candidates)',
  broad:      'Multi-entity or comparative queries (25 candidates)',
  exhaustive: 'Deep analysis across many documents (50 candidates)',
  mega:       'Maximum recall — "find all" enumeration (150 candidates)',
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const cleanMsg = (msg: string) => msg.replace(/\.{2,}$/, '').trimEnd();

const STEP_LABELS: Record<string, string> = { strategy_done: 'Strategy' };
const stepLabel = (step: string) => STEP_LABELS[step] ?? capitalize(step.replace(/_/g, ' '));

/**
 * One line per pipeline step, keeping the latest event of each (heartbeats
 * rewrite a step's message) at its first position. `strategy_done` is one
 * event per strategy or agent tool call, so all of them are kept. The
 * planning step is dropped when the Plan section already covers it.
 */
function collapseSteps(stepDetails: StepDetail[] | undefined, hasPlan: boolean): StepDetail[] {
  const out: StepDetail[] = [];
  const at = new Map<string, number>();
  for (const d of stepDetails ?? []) {
    if (d.step === 'planning' && hasPlan) continue;
    const i = d.step === 'strategy_done' ? undefined : at.get(d.step);
    if (i != null) {
      out[i] = d;
    } else {
      if (d.step !== 'strategy_done') at.set(d.step, out.length);
      out.push(d);
    }
  }
  return out;
}

/* ── StrategyContribution ────────────────────────────────────────────── */

interface ContribRow {
  name: string;
  count: number;        // final sources credited to this strategy (0 possible)
  share: number;        // count / total
  plannedPct?: number;  // planner's intent weight (0–100), may be undefined
}

/**
 * Per-strategy composition of the FINAL answer sources (grouped by
 * `retrieved_by`), with the planner's intent shown as a faint secondary line.
 * The timeline's sub-rows say what each strategy *returned*; this says what
 * the answer actually *kept*. Strategies that ran but kept nothing are shown
 * greyed as "ran · nothing kept".
 *
 * Before sources arrive (streaming), falls back to the planned intent as the
 * primary line and swaps to contribution once `sources` exist. A chunk found
 * by >1 strategy is credited to one (noted in the caption tooltip).
 */
function StrategyContribution({
  planned,
  sources,
  streaming,
}: {
  planned: PlanningEvent['strategies'];
  sources?: SourceDoc[];
  streaming?: boolean;
}) {
  // Planner intent, normalized to percentages (don't assume weights sum to 1).
  // A strategy can be planned more than once (one entity_lookup per compared
  // entity): its weights add up and the row shows "×n".
  const plannedTotal = planned.reduce((s, p) => s + ((p.weight ?? 0) > 0 ? p.weight! : 0), 0) || 1;
  const plannedPctByName: Record<string, number> = {};
  const plannedTimes: Record<string, number> = {};
  for (const p of planned) {
    plannedTimes[p.name] = (plannedTimes[p.name] ?? 0) + 1;
    if ((p.weight ?? 0) > 0) {
      plannedPctByName[p.name] = (plannedPctByName[p.name] ?? 0) + (p.weight! / plannedTotal) * 100;
    }
  }
  const plannedNames = [...new Set(planned.map((p) => p.name))];
  const label = (name: string) =>
    (plannedTimes[name] ?? 0) > 1 ? `${displayName(name)} ×${plannedTimes[name]}` : displayName(name);

  // What the answer actually used: full-text passages grouped by
  // `retrieved_by`. Summary-card documents are counted separately.
  const used: Record<string, number> = {};
  for (const s of sources ?? []) {
    if (isSummarySource(s)) continue;
    const key = s.retrieved_by ?? 'unknown';
    used[key] = (used[key] ?? 0) + 1;
  }
  const docCounts = countSourceDocs(sources ?? []);
  const total = docCounts.passages;
  const haveSources = total > 0;

  if (!haveSources) {
    // ── Streaming / no sources yet: show planner intent as the primary line.
    const plannedRows = plannedNames
      .filter((name) => (plannedPctByName[name] ?? 0) > 0)
      .sort((a, b) => (plannedPctByName[b] ?? 0) - (plannedPctByName[a] ?? 0));
    if (plannedRows.length === 0) return null;

    return (
      <div className="malpedia-contrib">
        <div className="malpedia-contrib-caption" title="The planner's intended fusion weighting — decided before any document was fetched. The actual per-strategy contribution appears once retrieval completes.">
          {streaming ? 'Planned — retrieving…' : 'Planned strategy mix'}
        </div>
        <div className="malpedia-contrib-rows">
          {plannedRows.map((name) => {
            const pct = plannedPctByName[name];
            const family = familyOf(name);
            return (
              <div key={name} className="malpedia-contrib-row malpedia-contrib-row--planned">
                <span className="malpedia-contrib-swatch" data-strategy={name} data-family={family} aria-hidden="true" />
                <span className="malpedia-contrib-name" title={STRATEGY_TOOLTIPS[name]}>{label(name)}</span>
                <span className="malpedia-contrib-bar">
                  <span className="malpedia-contrib-fill" data-strategy={name} data-family={family} style={{ width: `${pct.toFixed(1)}%` }} />
                </span>
                <span className="malpedia-contrib-count">{pct.toFixed(0)}%</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Contribution view. Row set = union of planned names and used keys, so
  // composite-leaf / expansion strategies that aren't in the plan still show,
  // and planned-but-unused ones still show (greyed). Sort by count desc, then
  // by planned weight.
  const names = new Set<string>([...plannedNames, ...Object.keys(used)]);
  const rows: ContribRow[] = [...names]
    .map((name) => ({
      name,
      count: used[name] ?? 0,
      share: (used[name] ?? 0) / total,
      plannedPct: plannedPctByName[name],
    }))
    .sort((a, b) => b.count - a.count || (b.plannedPct ?? 0) - (a.plannedPct ?? 0));

  const plannedIntent = plannedNames
    .filter((name) => (plannedPctByName[name] ?? 0) > 0)
    .sort((a, b) => (plannedPctByName[b] ?? 0) - (plannedPctByName[a] ?? 0));

  return (
    <div className="malpedia-contrib">
      <div className="malpedia-contrib-caption" title="Per-strategy count of the passages the answer was built from, grouped by which strategy retrieved each. A chunk found by more than one strategy is credited to a single one. Summarized documents were only seen as summary cards, not read in full.">
        Answer built from {total} {total === 1 ? 'passage' : 'passages'} in {docCounts.full}{' '}
        {docCounts.full === 1 ? 'document' : 'documents'}
        {docCounts.summary > 0 && ` + ${docCounts.summary} summarized`}
      </div>
      <div className="malpedia-contrib-rows">
        {rows.map((row) => {
          const empty = row.count === 0;
          const family = familyOf(row.name);
          return (
            <div
              key={row.name}
              className={`malpedia-contrib-row${empty ? ' malpedia-contrib-row--empty' : ''}`}
            >
              <span
                className="malpedia-contrib-swatch"
                data-strategy={row.name}
                data-family={family}
                data-empty={empty ? 'true' : undefined}
                aria-hidden="true"
              />
              <span className="malpedia-contrib-name" title={STRATEGY_TOOLTIPS[row.name]}>
                {label(row.name)}
              </span>
              {empty ? (
                <>
                  <span className="malpedia-contrib-empty-mark" aria-hidden="true">·</span>
                  <span className="malpedia-contrib-empty-label">ran · nothing kept</span>
                </>
              ) : (
                <>
                  <span className="malpedia-contrib-bar">
                    <span
                      className="malpedia-contrib-fill"
                      data-strategy={row.name}
                      data-family={family}
                      style={{ width: `${(row.share * 100).toFixed(1)}%` }}
                    />
                  </span>
                  <span className="malpedia-contrib-count">{row.count}</span>
                </>
              )}
            </div>
          );
        })}
      </div>
      {plannedIntent.length > 0 && (
        <div className="malpedia-contrib-intent" title="The planner's intended fusion weighting — decided before any document was fetched. This is intent, not outcome; the rows above are what the answer actually used.">
          <span className="malpedia-contrib-intent-label">Plan intent:</span>{' '}
          {plannedIntent.map((name, i) => (
            <React.Fragment key={name}>
              {i > 0 && ' · '}
              {label(name)} {Math.round(plannedPctByName[name])}%
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── IocPivotBanner ──────────────────────────────────────────────────── */

/**
 * Banner shown when the plan injected an `ioc_pivot` strategy. Ties the pivot
 * action to the indicator(s) that triggered it, threaded in from the query's
 * `detected_iocs` event.
 */
function IocPivotBanner({ detectedIocs }: { detectedIocs?: DetectedIOC[] }) {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const ioc of detectedIocs ?? []) {
    const v = ioc.value;
    if (v && !seen.has(v)) {
      seen.add(v);
      values.push(v);
    }
  }

  return (
    <div className="malpedia-ioc-pivot-banner">
      <span className="malpedia-ioc-pivot-glyph" aria-hidden="true">⌖</span>
      <span className="malpedia-ioc-pivot-text">
        {values.length > 0 ? (
          <>
            Pivoted from indicator{values.length > 1 ? 's' : ''}:{' '}
            {values.map((v, i) => (
              <React.Fragment key={i}>
                {i > 0 && ', '}
                <code className="malpedia-ioc-pivot-value">{v}</code>
              </React.Fragment>
            ))}
          </>
        ) : (
          'Pivoted from a detected indicator'
        )}
      </span>
    </div>
  );
}

/* ── Per-stage deep-dive ─────────────────────────────────────────────── */

function DetailFacts({ facts }: { facts: [string, React.ReactNode, string?][] }) {
  if (facts.length === 0) return null;
  return (
    <dl className="malpedia-step-facts">
      {facts.map(([k, v, tip]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd title={tip}>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/** "Understanding query": which retrieval mode was picked and why. */
function RoutingDetail({ event }: { event: RoutingEvent }) {
  const facts: [string, React.ReactNode, string?][] = [
    [
      'Mode',
      <>
        {MODE_LABELS[event.mode] ?? event.mode}
        {event.confidence < 1 && (
          <span className="malpedia-step-facts-faint"> · {(event.confidence * 100).toFixed(0)}% confident</span>
        )}
      </>,
    ],
  ];
  return (
    <>
      <DetailFacts facts={facts} />
      {event.reasoning && <div className="malpedia-reasoning-block">{event.reasoning}</div>}
    </>
  );
}

/** "Planning": the planner's decision — entities, strategy mix, reasoning. */
function PlanDetail({
  event,
  profileEvent,
  detectedIocs,
  sources,
  streaming,
}: {
  event: PlanningEvent;
  profileEvent?: ProfileEvent | null;
  detectedIocs?: DetectedIOC[];
  sources?: SourceDoc[];
  streaming?: boolean;
}) {
  const sourceKey = event.source === 'heuristic' || event.source === 'manual' ? event.source : 'llm';
  const facts: [string, React.ReactNode, string?][] = [
    ['Query type', event.query_type, QUERY_TYPE_TOOLTIPS[event.query_type]],
    [
      'Planned by',
      sourceKey === 'llm' ? event.model || 'LLM' : sourceKey,
      SOURCE_TOOLTIPS[sourceKey],
    ],
  ];
  if (event.temporal_hint) facts.push(['Time', event.temporal_hint]);
  else if (event.date_from || event.date_to) facts.push(['Time', `${event.date_from ?? '…'} → ${event.date_to ?? '…'}`]);

  // Drop keywords that duplicate an entity chip (case-insensitive).
  const entityLowers = new Set(event.entities.map((e) => e.trim().toLowerCase()));
  const dedupedKeywords = event.keywords.filter((k) => !entityLowers.has(k.trim().toLowerCase()));
  const hasPivot = event.strategies.some((s) => s.name === 'ioc_pivot');

  return (
    <>
      <DetailFacts facts={facts} />
      {hasPivot && <IocPivotBanner detectedIocs={detectedIocs} />}
      {(event.entities.length > 0 || dedupedKeywords.length > 0) && (
        <div className="malpedia-retrieval-chips">
          {event.entities.map((e, i) => (
            <span key={`e-${i}`} className="malpedia-retrieval-chip">{e}</span>
          ))}
          {dedupedKeywords.map((k, i) => (
            <span key={`k-${i}`} className="malpedia-keyword-chip">{k}</span>
          ))}
        </div>
      )}
      {event.strategies.length > 0 && (
        <StrategyContribution planned={event.strategies} sources={sources} streaming={streaming} />
      )}
      {profileEvent && <ProfileBadges event={profileEvent} />}
      {event.reasoning && (
        <div className={`malpedia-reasoning-block${event.fallback ? ' malpedia-reasoning-block--error' : ''}`}>
          {event.reasoning}
        </div>
      )}
    </>
  );
}

/** Agent "Researching": what the model ended up reading, and its stop reason. */
function AgentDetail({ event, sources }: { event: PlanningEvent; sources?: SourceDoc[] }) {
  const c = countSourceDocs(sources ?? []);
  const facts: [string, React.ReactNode, string?][] = [];
  if (event.model) facts.push(['Model', event.model, SOURCE_TOOLTIPS.agent]);
  if (c.full > 0 || c.summary > 0) {
    facts.push([
      'Read',
      `${c.full} ${c.full === 1 ? 'document' : 'documents'} in full${c.summary > 0 ? ` + ${c.summary} summarized` : ''}`,
      'Documents whose passages the answer model read, and documents it only saw as summary cards.',
    ]);
  }
  return (
    <>
      <DetailFacts facts={facts} />
      {event.reasoning && <div className="malpedia-reasoning-block">{event.reasoning}</div>}
    </>
  );
}

function ScoreThresholdDetail({ threshold }: { threshold: ScoreThreshold }) {
  return (
    <div className="malpedia-score-threshold">
      <div className="malpedia-score-threshold-row">
        {threshold.min_score != null && (
          <span className="malpedia-score-threshold-item">
            <span className="malpedia-score-threshold-label">weakest kept</span>
            <span className="malpedia-score-threshold-value">{threshold.min_score.toFixed(2)}</span>
          </span>
        )}
        {threshold.cut_score != null && (
          <span className="malpedia-score-threshold-item malpedia-score-threshold-item--cut">
            <span className="malpedia-score-threshold-label">first dropped</span>
            <span className="malpedia-score-threshold-value">{threshold.cut_score.toFixed(2)}</span>
          </span>
        )}
        {threshold.cutoff_dropped > 0 && (
          <span className="malpedia-score-threshold-item">
            <span className="malpedia-score-threshold-label">dropped (low score)</span>
            <span className="malpedia-score-threshold-value">{threshold.cutoff_dropped}</span>
          </span>
        )}
      </div>
    </div>
  );
}

/* ── ProfileBadges ───────────────────────────────────────────────────── */

function ProfileBadges({ event }: { event: ProfileEvent }) {
  const DEPTH_NAMES: Record<string, string> = {
    auto: 'Auto', narrow: 'Quick', normal: 'Balanced', broad: 'Thorough',
    manual: 'Custom',
    exhaustive: 'Deep', mega: 'Sweep',
  };
  return (
    <div className="malpedia-profile-badges">
      <span className="malpedia-profile-chip" data-tooltip={DEPTH_TOOLTIPS[event.depth]}>{DEPTH_NAMES[event.depth] ?? event.depth}</span>
      <span className="malpedia-profile-chip">Candidates {event.top_k}</span>
      <span className="malpedia-profile-chip">Merge {event.fusion_cap}</span>
      <span className="malpedia-profile-chip">Context {event.top_sources}</span>
    </div>
  );
}

/* ── Markdown export helper ──────────────────────────────────────────── */

function buildScoreTableMd(
  sources: SourceDoc[] | undefined,
  scoreThreshold: ScoreThreshold | null | undefined,
): string {
  if (!sources?.length && !scoreThreshold?.excluded_sources?.length) return '';

  const included = (sources || []).map((s, i) => ({
    rank: i + 1,
    cite_key: s.cite_key,
    score: s.score,
    title: s.title ?? '',
    status: isSummarySource(s) ? 'summary only' : 'included',
  }));
  const excluded = (scoreThreshold?.excluded_sources || []).map((s, i) => ({
    rank: included.length + i + 1,
    cite_key: s.cite_key,
    score: s.score,
    title: s.title ?? '',
    status: 'excluded',
  }));
  const rows = [...included, ...excluded];
  if (rows.length === 0) return '';

  let md = `## Source Scores\n\n`;
  md += `| # | Cite Key | Score | Title | Status |\n`;
  md += `|---|----------|-------|-------|--------|\n`;
  for (const r of rows) {
    md += `| ${r.rank} | ${r.cite_key} | ${r.score.toFixed(3)} | ${r.title} | ${r.status} |\n`;
  }
  md += '\n';
  return md;
}

/**
 * "Answer built from N sources" table — final sources grouped by the strategy
 * that retrieved them (`retrieved_by`). Mirrors the on-screen StrategyContribution:
 * the count is the truth, planned weight is intent. A chunk found by multiple
 * strategies is credited to one.
 */
function buildContributionMd(sources: SourceDoc[] | undefined): string {
  if (!sources?.length) return '';
  const used: Record<string, number> = {};
  for (const s of sources) {
    if (isSummarySource(s)) continue;
    const key = s.retrieved_by ?? 'unknown';
    used[key] = (used[key] ?? 0) + 1;
  }
  const c = countSourceDocs(sources);
  const total = c.passages;
  if (total === 0) return '';
  const rows = Object.entries(used).sort((a, b) => b[1] - a[1]);

  let md = `\n### Answer built from ${total} passage${total === 1 ? '' : 's'} in ${c.full} document${c.full === 1 ? '' : 's'}`;
  md += c.summary > 0 ? ` + ${c.summary} summarized\n\n` : '\n\n';
  md += `| Strategy | Sources | Share |\n|---|---|---|\n`;
  for (const [name, count] of rows) {
    md += `| ${name} | ${count} | ${((count / total) * 100).toFixed(0)}% |\n`;
  }
  md += `\n_Sources are grouped by the strategy that retrieved them; a chunk found by more than one strategy is credited to a single one._\n`;
  return md;
}

export function buildRetrievalMd(
  completedSteps: PipelineStep[] | undefined,
  stepDetails: StepDetail[] | undefined,
  planningEvent: PlanningEvent | null | undefined,
  routingEvent: RoutingEvent | null | undefined,
  profileEvent: ProfileEvent | null | undefined,
  sources?: SourceDoc[],
  scoreThreshold?: ScoreThreshold | null,
  detectedIocs?: DetectedIOC[],
): string {
  let md = '';

  if (routingEvent) {
    md += `## Routing\n\n`;
    md += `**Mode:** ${routingEvent.mode} · ${(routingEvent.confidence * 100).toFixed(0)}% confidence\n\n`;
    if (routingEvent.reasoning) md += `> ${routingEvent.reasoning}\n\n`;
  }

  if (planningEvent) {
    const pe = planningEvent;
    md += `## Planning\n\n`;
    md += `**Query type:** ${pe.query_type}`;
    if (pe.temporal_hint) md += ` · **Temporal:** ${pe.temporal_hint}`;
    md += '\n';
    if (pe.entities.length > 0) md += `**Entities:** ${pe.entities.join(', ')}\n`;
    if (pe.keywords.length > 0) md += `**Keywords:** ${pe.keywords.join(', ')}\n`;
    if (pe.source === 'agent' && (pe.agent_trace?.length ?? 0) > 0) {
      md += `\n### Tool calls\n\n| # | Tool | Arguments | Documents |\n|---|---|---|---|\n`;
      pe.agent_trace!.forEach((t, i) => {
        const args = toolArgSummary(t.tool, t.args).replace(/\|/g, '\\|');
        md += `| ${i + 1} | ${t.tool} | ${args} | ${t.error ? `error: ${t.error}` : t.docs ?? ''} |\n`;
      });
    } else if (pe.strategies.length > 0) {
      md += `\n### Strategies\n\n| Strategy | Weight |\n|---|---|\n`;
      for (const s of pe.strategies) {
        md += `| ${s.name} | ${s.weight != null ? `${(s.weight * 100).toFixed(0)}%` : ''} |\n`;
      }
    }
    md += buildContributionMd(sources);
    if (pe.strategies.some((s) => s.name === 'ioc_pivot') && detectedIocs?.length) {
      const seen = new Set<string>();
      const values = detectedIocs
        .map((i) => i.value)
        .filter((v) => v && !seen.has(v) && seen.add(v));
      if (values.length > 0) {
        md += `\n**Pivoted from:** ${values.map((v) => `\`${v}\``).join(', ')}\n`;
      }
    }
    if (pe.reasoning) {
      md += `\n### ${pe.fallback ? 'Error' : 'Reasoning'}\n\n${pe.reasoning}\n`;
    }
    md += '\n';
  }

  if (profileEvent) {
    const depthName: Record<string, string> = { auto: 'Auto', narrow: 'Quick', normal: 'Balanced', broad: 'Thorough', exhaustive: 'Deep', mega: 'Sweep', manual: 'Custom' };
    md += `## Profile\n\n`;
    md += `**Depth:** ${depthName[profileEvent.depth] ?? profileEvent.depth} · **Candidates:** ${profileEvent.top_k} · **Merge limit:** ${profileEvent.fusion_cap} · **Context size:** ${profileEvent.top_sources}\n\n`;
  }

  if (stepDetails?.length) {
    md += `## Steps\n\n`;
    for (const d of collapseSteps(stepDetails, !!planningEvent)) {
      const msg = cleanMsg(d.message);
      md += `- **${stepLabel(d.step)}:** ${msg}${d.count != null ? ` _(${d.count})_` : ''}\n`;
    }
    md += '\n';
  } else if (completedSteps?.length) {
    md += `**Pipeline:** ${completedSteps.join(' → ')}\n\n`;
  }

  md += buildScoreTableMd(sources, scoreThreshold);

  return md;
}

/** Trigger a client-side download of a markdown blob. */
function downloadMarkdown(filename: string, md: string): void {
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}


/* ── Timeline marks ──────────────────────────────────────────────────── */

interface MarksState extends TimelineMarks {
  firstSeen: Record<string, number>;
  wasStreaming: boolean;
}

/**
 * Client-side times the progress events don't carry: when the turn was sent,
 * the first time each raw step was seen (the live message state overwrites a
 * step's `ts` with every heartbeat) and when the stream ended. Only known for
 * turns streamed while this card was mounted; stored turns use event `ts`.
 */
function useTimelineMarks(streaming: boolean, stepDetails: StepDetail[] | undefined): TimelineMarks {
  const ref = useRef<MarksState>({ firstSeen: {}, wasStreaming: false });
  const m = ref.current;
  if (streaming) {
    if (!m.wasStreaming) {
      // Mounted mid-stream (chat switched back): the send time is unknown.
      m.startedAt = stepDetails?.length ? undefined : Date.now() / 1000;
      m.firstSeen = {};
      m.endedAt = undefined;
    }
    for (const d of stepDetails ?? []) {
      if (d.ts != null && m.firstSeen[d.step] == null) m.firstSeen[d.step] = d.ts;
    }
  } else if (m.wasStreaming) {
    m.endedAt = Date.now() / 1000;
  }
  m.wasStreaming = streaming;
  return m;
}

/** Ticking "4.2 s" — isolated so only this span re-renders every tick. */
function LiveElapsed({ since }: { since: number }) {
  const now = useNowSeconds(true);
  return (
    <span className="malpedia-retrieval-card-elapsed" aria-hidden="true">
      {formatSeconds(Math.max(0, now - since))}
    </span>
  );
}

function stageDetails(
  timeline: Timeline,
  props: Pick<
    RetrievalCardProps,
    'routingEvent' | 'planningEvent' | 'profileEvent' | 'scoreThreshold' | 'sources' | 'detectedIocs' | 'streaming'
  >,
): Partial<Record<StageKey, React.ReactNode>> {
  const out: Partial<Record<StageKey, React.ReactNode>> = {};
  const add = (key: StageKey, node: React.ReactNode) => {
    out[key] = out[key] ? <>{out[key]}{node}</> : node;
  };
  const has = (key: StageKey) => timeline.stages.some((s) => s.key === key);
  const { routingEvent, planningEvent, scoreThreshold } = props;

  if (routingEvent && has('understand') && (routingEvent.reasoning || routingEvent.confidence < 1)) {
    add('understand', <RoutingDetail event={routingEvent} />);
  }
  if (planningEvent?.source === 'agent') {
    if (has('search')) add('search', <AgentDetail event={planningEvent} sources={props.sources} />);
  } else if (planningEvent && has('plan')) {
    add(
      'plan',
      <PlanDetail
        event={planningEvent}
        profileEvent={props.profileEvent}
        detectedIocs={props.detectedIocs}
        sources={props.sources}
        streaming={props.streaming}
      />,
    );
  }
  if (scoreThreshold && (scoreThreshold.min_score != null || scoreThreshold.cut_score != null)) {
    const host: StageKey | undefined = has('rerank') ? 'rerank' : has('search') ? 'search' : undefined;
    if (host) add(host, <ScoreThresholdDetail threshold={scoreThreshold} />);
  }
  return out;
}

/* ── RetrievalCard ───────────────────────────────────────────────────── */

interface RetrievalCardProps {
  streaming?: boolean;
  hasContent?: boolean;
  progressStep?: PipelineStep | null;
  progressMessage?: string;
  progressCount?: number;
  completedSteps?: PipelineStep[];
  stepDetails?: StepDetail[];
  planningEvent?: PlanningEvent | null;
  routingEvent?: RoutingEvent | null;
  graphTraversalEvent?: GraphTraversalEvent | null;
  profileEvent?: ProfileEvent | null;
  scoreThreshold?: ScoreThreshold | null;
  sources?: SourceDoc[];
  planningTokens?: string;
  detectedIocs?: DetectedIOC[];
  /** Length of the answer so far (live token estimate). */
  answerChars?: number;
}

export function RetrievalCard(props: RetrievalCardProps) {
  const {
    streaming,
    hasContent,
    completedSteps,
    stepDetails,
    planningEvent,
    routingEvent,
    profileEvent,
    scoreThreshold,
    sources,
    planningTokens,
    detectedIocs,
  } = props;
  const isThinking = !!streaming && !hasContent;
  const [open, setOpen] = useState(isThinking);
  const bodyId = useId();

  // Open while retrieval runs; fold into the one-line summary as soon as the
  // answer starts streaming (and when the stream ends). Stored turns start
  // folded.
  const prevStreamingRef = useRef(streaming);
  const prevThinkingRef = useRef(isThinking);
  useEffect(() => {
    if (isThinking && !prevThinkingRef.current) setOpen(true);
    if (!isThinking && prevThinkingRef.current) setOpen(false);
    if (prevStreamingRef.current && !streaming) setOpen(false);
    prevStreamingRef.current = streaming;
    prevThinkingRef.current = isThinking;
  }, [streaming, isThinking]);

  const marks = useTimelineMarks(!!streaming, stepDetails);
  const timeline = deriveTimeline({
    streaming,
    hasContent,
    stepDetails,
    planningEvent,
    routingEvent,
    sources,
    marks,
    answerChars: props.answerChars,
  });

  const usedWeb =
    !!stepDetails?.some((d) => d.step.includes('web_search')) ||
    !!sources?.some((s) => s.cite_key.startsWith('web:') || s.retrieved_by === 'web_search');

  const handleExport = useCallback(() => {
    try {
      const now = new Date();
      const ts = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const dateStr = now.toISOString().slice(0, 16).replace('T', ' ');
      const fileName = `Retrieval-${ts}.md`;
      const md =
        `# Retrieval Record — ${dateStr}\n\n` +
        buildRetrievalMd(
          completedSteps,
          stepDetails,
          planningEvent,
          routingEvent,
          profileEvent,
          sources,
          scoreThreshold,
          detectedIocs,
        );
      downloadMarkdown(fileName, md);
      toast.success('Retrieval record downloaded');
    } catch (e) {
      toast.error(`Failed to export retrieval: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [completedSteps, stepDetails, planningEvent, routingEvent, profileEvent, sources, scoreThreshold, detectedIocs]);

  const isLlmOnly = routingEvent?.mode === 'llm_only';
  if (!isLlmOnly && timeline.stages.length === 0) return null;
  if (isLlmOnly && !streaming && !hasContent) return null;

  const exportButton = !streaming && (
    <button
      type="button"
      className="malpedia-retrieval-card-export"
      onClick={handleExport}
      title="Download retrieval record"
      aria-label="Download retrieval record"
    >
      ⬇
    </button>
  );

  if (isLlmOnly) {
    return (
      <div className="malpedia-retrieval-card">
        <div className="malpedia-retrieval-card-bar">
          <span className="malpedia-retrieval-card-toggle" data-static="true">
            <span className="malpedia-retrieval-card-summary" title="No document retrieval was performed for this turn">
              Chat only — no retrieval
            </span>
          </span>
          {exportButton}
        </div>
      </div>
    );
  }

  const active = timeline.active;
  const n = timeline.stages.length;
  const stepNo = active ? timeline.stages.indexOf(active) + 1 : 0;
  const headline =
    isThinking && active
      ? open
        ? `Step ${stepNo} of ${n}`
        : `${active.label} · step ${stepNo} of ${n}`
      : timelineSummary(timeline) || 'Retrieval';
  const elapsedSince = isThinking ? timeline.startTs : undefined;
  const headStatus = streaming ? 'active' : 'done';

  return (
    <div className="malpedia-retrieval-card" data-live={streaming ? 'true' : undefined}>
      <div className="malpedia-retrieval-card-bar">
        <button
          type="button"
          className="malpedia-retrieval-card-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={bodyId}
          title={open ? 'Hide retrieval steps' : 'Show retrieval steps'}
        >
          <StepIcon status={headStatus} small />
          <span
            className="malpedia-retrieval-card-summary"
            title={
              isThinking ? undefined
              : timeline.timed && timeline.llmS + timeline.workS > 0
                ? 'Time waiting for the language model · time spent on everything else · sources'
                : timeline.retrievalS != null ? 'Steps · time until the answer started · sources' : undefined
            }
          >
            {headline}
          </span>
          {elapsedSince != null && <LiveElapsed since={elapsedSince} />}
          {usedWeb && (
            <span className="malpedia-retrieval-bar-web" title="Web search was used">
              web
            </span>
          )}
          <span className="malpedia-retrieval-card-chevron" aria-hidden="true">▾</span>
        </button>
        {exportButton}
      </div>

      {/* Screen readers hear stage changes, not every token or tick. */}
      <div className="malpedia-sr-only" aria-live="polite">
        {isThinking && active ? `${active.label}${active.sublabel ? `, ${active.sublabel}` : ''}` : ''}
      </div>

      <div id={bodyId} className={`malpedia-step-collapse malpedia-retrieval-card-body${open ? ' open' : ''}`}>
        <div className="malpedia-step-collapse-inner">
          <div className="malpedia-retrieval-card-details">
            <StepTimeline
              timeline={timeline}
              planningTokens={planningTokens}
              details={stageDetails(timeline, props)}
              visible={open}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
